import React, { useEffect, useRef, useState } from 'react';
import {
	Fullscreen,
	FullscreenExit,
	Pause,
	PlayArrow,
	VolumeDown,
	VolumeOff,
	VolumeUp,
} from '@material-ui/icons';
import { Slider } from '@material-ui/core';

const MjpegControls = ({ 
	boxRef, 
	showControls, 
	room, 
	onVolumeChange, 
	isPlaying, 
	onPlayPause
}) => {
	const [volume, setVolume] = useState(50);
	const [isMuted, setIsMuted] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);

	const handleVolumeChange = (event, newValue) => {
		setVolume(newValue);
		setIsMuted(newValue === 0);
		if (onVolumeChange) {
			onVolumeChange(newValue / 100);
		}
	};

	const handleMuteToggle = () => {
		const newMuted = !isMuted;
		setIsMuted(newMuted);
		if (onVolumeChange) {
			onVolumeChange(newMuted ? 0 : volume / 100);
		}
	};

	const handleFullscreenToggle = () => {
		const element = boxRef.current;
		if (!element) return;

		if (!isFullscreen) {
			if (element.requestFullscreen) {
				element.requestFullscreen();
			} else if (element.webkitRequestFullscreen) {
				element.webkitRequestFullscreen();
			} else if (element.msRequestFullscreen) {
				element.msRequestFullscreen();
			}
		} else {
			if (document.exitFullscreen) {
				document.exitFullscreen();
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
			} else if (document.msExitFullscreen) {
				document.msExitFullscreen();
			}
		}
	};

	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);
		document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
		document.addEventListener('msfullscreenchange', handleFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
			document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
			document.removeEventListener('msfullscreenchange', handleFullscreenChange);
		};
	}, []);

	const getVolumeIcon = () => {
		if (isMuted || volume === 0) {
			return <VolumeOff />;
		} else if (volume < 50) {
			return <VolumeDown />;
		} else {
			return <VolumeUp />;
		}
	};

	return (
		<div
			className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 transition-opacity duration-300 ${
				showControls ? 'opacity-100' : 'opacity-0'
			}`}
			style={{ pointerEvents: showControls ? 'auto' : 'none' }}
		>
			<div className="flex items-center justify-between text-white">
				<div className="flex items-center space-x-4">
					{/* Play/Pause button - only show when we have audio */}
					{room?.audio_url && (
						<button
							className="hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
							onClick={onPlayPause}
							type="button"
						>
							{isPlaying ? <Pause /> : <PlayArrow />}
						</button>
					)}

					{/* Volume controls - only show if we have audio */}
					{room?.audio_url && (
						<div className="flex items-center space-x-2">
							<button
								className="hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
								onClick={handleMuteToggle}
								type="button"
							>
								{getVolumeIcon()}
							</button>
							<div className="w-24">
								<Slider
									max={100}
									min={0}
									onChange={handleVolumeChange}
									value={isMuted ? 0 : volume}
									valueLabelDisplay="auto"
									style={{ color: 'white' }}
								/>
							</div>
						</div>
					)}
				</div>

				<div className="flex items-center space-x-2">
					{/* Live indicator */}
					<div className="bg-red-600 text-white px-2 py-1 rounded text-sm font-bold">
						LIVE
					</div>

					{/* Fullscreen button */}
					<button
						className="hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
						onClick={handleFullscreenToggle}
						type="button"
					>
						{isFullscreen ? <FullscreenExit /> : <Fullscreen />}
					</button>
				</div>
			</div>
		</div>
	);
};

export default MjpegControls;